// src/pages/Officer/OfficerAlerts.jsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Stack,
  Chip,
  IconButton,
  TextField,
  InputAdornment,
  CircularProgress,
  Tabs,
  Tab,
  alpha,
  Tooltip,
  Collapse,
  Pagination,
  Divider,
  Alert,
} from '@mui/material';
import {
  Search,
  TrendingUp,
  TrendingDown,
  Refresh,
  Download,
  ExpandMore,
  ExpandLess,
  CheckCircle,
  AccessTime,
  Speed,
  Timeline,
  ErrorOutline,
} from '@mui/icons-material';
import { alertAPI } from '../../services/api';
import useAlertWebSocket from '../../hooks/useAlertWebSocket';
import { useNotifications } from '../../hooks/useNotifications';

/**
 * AlertCard Component - Individual alert display
 */
const AlertCard = ({ alert }) => {
  const [expanded, setExpanded] = useState(false);

  const getTypeColor = (type) => {
    if (!type) return '#00d4ff';
    const upperType = type.toUpperCase();
    if (upperType === 'BUY' || upperType === 'LONG' || upperType === 'ENTRY') return '#00ff88';
    if (upperType === 'SELL' || upperType === 'SHORT') return '#ff4444';
    return '#00d4ff';
  };

  const getStatusColor = (status) => {
    if (!status) return '#64748b';
    if (status === 'active') return '#3b82f6';
    if (status === 'executed') return '#fb923c';
    return '#64748b';
  };

  const alertType = alert.type || 'SIGNAL';
  const TypeIcon = (alertType === 'BUY' || alertType === 'LONG' || alertType === 'ENTRY') 
    ? TrendingUp 
    : TrendingDown;

  return (
    <Paper
      elevation={0}
      sx={{
        bgcolor: alpha('#0f1419', 0.6),
        border: '1px solid',
        borderColor: alpha(getTypeColor(alertType), 0.2),
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'all 0.2s ease',
        '&:hover': {
          borderColor: alpha(getTypeColor(alertType), 0.4),
          boxShadow: `0 4px 12px ${alpha(getTypeColor(alertType), 0.15)}`,
        },
      }}
    >
      {/* Main Content */}
      <Box
        sx={{
          p: 2.5,
          bgcolor: alpha(getTypeColor(alertType), 0.03),
        }}
      >
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          {/* Left: Icon & Info */}
          <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ flex: 1 }}>
            {/* Icon */}
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: 1.5,
                bgcolor: alpha(getTypeColor(alertType), 0.15),
                border: '2px solid',
                borderColor: alpha(getTypeColor(alertType), 0.3),
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
              }}
            >
              <TypeIcon sx={{ color: getTypeColor(alertType), fontSize: 24 }} />
            </Box>

            {/* Info */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Symbol & Type */}
              <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: 'grey.100',
                    fontFamily: 'monospace',
                    fontSize: '1.1rem',
                  }}
                >
                  {alert.symbol || 'UNKNOWN'}
                </Typography>
                <Chip
                  label={alertType}
                  size="small"
                  sx={{
                    height: 24,
                    fontWeight: 800,
                    fontSize: '0.7rem',
                    bgcolor: alpha(getTypeColor(alertType), 0.2),
                    color: getTypeColor(alertType),
                    border: '1px solid',
                    borderColor: alpha(getTypeColor(alertType), 0.4),
                  }}
                />
                <Chip
                  label={alert.status || 'active'}
                  size="small"
                  sx={{
                    height: 22,
                    fontWeight: 600,
                    fontSize: '0.65rem',
                    textTransform: 'capitalize',
                    bgcolor: alpha(getStatusColor(alert.status), 0.15),
                    color: getStatusColor(alert.status),
                    border: '1px solid',
                    borderColor: alpha(getStatusColor(alert.status), 0.3),
                  }}
                />
              </Stack>

              {/* Strategy & Condition */}
              <Stack spacing={0.5} sx={{ mb: 1.5 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography
                    variant="caption"
                    sx={{
                      color: 'grey.600',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.05em',
                    }}
                  >
                    Strategy:
                  </Typography>
                  <Typography
                    sx={{
                      color: 'grey.300',
                      fontSize: '0.85rem',
                      fontWeight: 600,
                      fontFamily: 'monospace',
                    }}
                  >
                    {alert.strategy || alert.strategy_id || 'N/A'}
                  </Typography>
                </Stack>

                {alert.condition_name && (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography
                      variant="caption"
                      sx={{
                        color: 'grey.600',
                        fontSize: '0.7rem',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                      }}
                    >
                      Condition:
                    </Typography>
                    <Typography
                      sx={{
                        color: 'grey.400',
                        fontSize: '0.8rem',
                        fontFamily: 'monospace',
                      }}
                    >
                      {alert.condition_name}
                    </Typography>
                  </Stack>
                )}
              </Stack>

              {/* Timestamp */}
              <Stack direction="row" spacing={1} alignItems="center">
                <AccessTime sx={{ fontSize: 14, color: 'grey.600' }} />
                <Typography
                  variant="caption"
                  sx={{
                    color: 'grey.500',
                    fontFamily: 'monospace',
                    fontSize: '0.75rem',
                  }}
                >
                  {new Date(alert.timestamp || alert.detected_at).toLocaleString()}
                  {alert.time && ` • ${alert.time}`}
                </Typography>
              </Stack>
            </Box>
          </Stack>

          {/* Right: Expand Button */}
          <IconButton
            size="small"
            onClick={() => setExpanded(!expanded)}
            sx={{
              color: 'grey.500',
              ml: 2,
              '&:hover': {
                bgcolor: alpha('#00d4ff', 0.1),
                color: '#00d4ff',
              },
            }}
          >
            {expanded ? <ExpandLess /> : <ExpandMore />}
          </IconButton>
        </Stack>
      </Box>

      {/* Expanded Details */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Divider sx={{ borderColor: alpha(getTypeColor(alertType), 0.1) }} />
        <Box
          sx={{
            p: 2.5,
            bgcolor: alpha('#0a0e14', 0.6),
          }}
        >
          <Stack spacing={2}>
            <Typography
              variant="caption"
              sx={{
                color: '#00d4ff',
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.1em',
                fontSize: '0.7rem',
              }}
            >
              Signal Details
            </Typography>

            {alert.signal_data && Object.keys(alert.signal_data).length > 0 ? (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: alpha('#000', 0.4),
                  border: '1px solid',
                  borderColor: alpha('#00d4ff', 0.2),
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    fontSize: '0.75rem',
                    color: '#a0aec0',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.6,
                  }}
                >
                  {JSON.stringify(alert.signal_data, null, 2)}
                </pre>
              </Box>
            ) : (
              <Box
                sx={{
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: alpha('#000', 0.4),
                  border: '1px solid',
                  borderColor: alpha('#00d4ff', 0.2),
                }}
              >
                <Typography 
                  sx={{ 
                    fontSize: '0.8rem', 
                    color: 'grey.600', 
                    fontStyle: 'italic',
                    textAlign: 'center',
                  }}
                >
                  No additional signal data available
                </Typography>
              </Box>
            )}

            {/* Raw Alert Data for Debugging */}
            <details style={{ marginTop: '1rem' }}>
              <summary style={{ 
                cursor: 'pointer', 
                color: '#64748b',
                fontSize: '0.7rem',
                fontFamily: 'monospace',
              }}>
                View Raw Data (Debug)
              </summary>
              <Box
                sx={{
                  mt: 1,
                  p: 2,
                  borderRadius: 1.5,
                  bgcolor: alpha('#000', 0.4),
                  border: '1px solid',
                  borderColor: alpha('#666', 0.2),
                }}
              >
                <pre
                  style={{
                    margin: 0,
                    fontSize: '0.7rem',
                    color: '#64748b',
                    fontFamily: 'monospace',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    lineHeight: 1.6,
                  }}
                >
                  {JSON.stringify(alert, null, 2)}
                </pre>
              </Box>
            </details>
          </Stack>
        </Box>
      </Collapse>
    </Paper>
  );
};

/**
 * OfficerAlerts - Main Component
 */
const OfficerAlerts = () => {
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [activeTab, setActiveTab] = useState(0);
  const [alerts, setAlerts] = useState([]);
  const [totalAlerts, setTotalAlerts] = useState(0);
  const [stats, setStats] = useState(null);

  const { showNotification, playNotificationSound } = useNotifications(true);

  /**
   * Fetch alerts from API
   */
  const fetchAlerts = useCallback(async (showLoader = true) => {
    try {
      if (showLoader) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }
      setError(null);

      console.log('[OfficerAlerts] Fetching alerts with params:', {
        page,
        limit: pageSize,
        tab: activeTab,
      });

      const result = await alertAPI.getHistoricalAlerts({
        page: page,
        limit: pageSize,
      });

      console.log('[OfficerAlerts] API Response:', result);

      if (result.success) {
        const fetchedAlerts = result.data.alerts || [];
        console.log('[OfficerAlerts] Fetched alerts:', fetchedAlerts.length);
        
        setAlerts(fetchedAlerts);
        setTotalAlerts(result.data.total || fetchedAlerts.length);
      } else {
        console.error('[OfficerAlerts] API Error:', result.message);
        setError(result.message || 'Failed to fetch alerts');
      }
    } catch (err) {
      console.error('[OfficerAlerts] Exception:', err);
      setError(err.message || 'An unexpected error occurred');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [page, pageSize, activeTab]);

  /**
   * Fetch statistics
   */
  const fetchStats = useCallback(async () => {
    try {
      console.log('[OfficerAlerts] Fetching stats...');
      const result = await alertAPI.getAlertStats('7d');
      console.log('[OfficerAlerts] Stats response:', result);
      
      if (result.success) {
        setStats(result.data);
      }
    } catch (err) {
      console.error('[OfficerAlerts] Stats error:', err);
    }
  }, []);

  /**
   * WebSocket handlers for live updates
   */
  const handleConditionUpdate = useCallback((data) => {
    console.log('[OfficerAlerts] Condition Update:', data);
  }, []);

  const handleAlertTriggered = useCallback((data) => {
    console.log('[OfficerAlerts] New Alert Triggered:', data);
    
    // Add new alert to list
    const newAlert = {
      id: Date.now(),
      timestamp: new Date().toISOString(),
      detected_at: new Date().toISOString(),
      time: 'Just now',
      status: 'active',
      ...data
    };
    
    setAlerts(prev => [newAlert, ...prev.slice(0, pageSize - 1)]);
    setTotalAlerts(prev => prev + 1);

    // Notifications
    playNotificationSound('alert', 'high');
    showNotification({
      title: `New Alert: ${data.strategy_id || 'Unknown'}`,
      body: `${data.alerts?.length || 1} condition(s) met`,
    });
  }, [pageSize, playNotificationSound, showNotification]);

  const { isConnected } = useAlertWebSocket(
    handleConditionUpdate,
    handleAlertTriggered
  );

  /**
   * Initial fetch
   */
  useEffect(() => {
    console.log('[OfficerAlerts] Component mounted, fetching data...');
    fetchAlerts(true);
    fetchStats();
  }, [fetchAlerts, fetchStats]);

  /**
   * Handlers
   */
  const handlePageChange = (event, value) => {
    console.log('[OfficerAlerts] Page changed to:', value);
    setPage(value);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRefresh = () => {
    console.log('[OfficerAlerts] Manual refresh triggered');
    fetchAlerts(false);
    fetchStats();
  };

  const handleTabChange = (event, newValue) => {
    console.log('[OfficerAlerts] Tab changed to:', newValue);
    setActiveTab(newValue);
    setPage(1);
  };

  /**
   * Filter alerts by search query and tab
   */
  const filteredAlerts = alerts.filter((alert) => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matchesSearch = 
        alert.symbol?.toLowerCase().includes(query) ||
        alert.strategy?.toLowerCase().includes(query) ||
        alert.strategy_id?.toLowerCase().includes(query) ||
        alert.condition_name?.toLowerCase().includes(query);
      if (!matchesSearch) return false;
    }

    // Tab filter
    if (activeTab === 0) return true; // All
    if (activeTab === 1) return alert.status === 'active';
    if (activeTab === 2) return alert.status === 'executed';
    if (activeTab === 3) return alert.status === 'closed';
    return true;
  });

  console.log('[OfficerAlerts] Filtered alerts:', filteredAlerts.length, 'out of', alerts.length);

  const totalPages = Math.ceil(totalAlerts / pageSize);

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: '#0a0e14',
          gap: 2,
        }}
      >
        <CircularProgress size={48} thickness={4} sx={{ color: '#00d4ff' }} />
        <Typography variant="body1" sx={{ color: 'grey.400' }}>
          Loading alerts...
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: '#0a0e14',
        minHeight: '100vh',
        color: 'grey.100',
      }}
    >
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 100,
          bgcolor: alpha('#0f1419', 0.95),
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid',
          borderColor: alpha('#00d4ff', 0.1),
        }}
      >
        <Container maxWidth={false} sx={{ maxWidth: 1920 }}>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ py: 2 }}
          >
            {/* Left: Title */}
            <Stack direction="row" alignItems="center" spacing={2}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  background: 'linear-gradient(135deg, #00d4ff 0%, #0066ff 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${alpha('#00d4ff', 0.3)}`,
                }}
              >
                <CheckCircle sx={{ color: 'white', fontSize: 24 }} />
              </Box>
              <Box>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    letterSpacing: '-0.02em',
                    color: 'grey.50',
                  }}
                >
                  Alerts & Signals
                </Typography>
                <Typography variant="caption" sx={{ color: 'grey.500' }}>
                  {totalAlerts} total alerts
                </Typography>
              </Box>
            </Stack>

            {/* Right: Controls */}
            <Stack direction="row" spacing={1.5} alignItems="center">
              {/* Connection Status */}
              <Chip
                label={isConnected ? 'LIVE' : 'OFFLINE'}
                size="small"
                sx={{
                  height: 28,
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  letterSpacing: '0.05em',
                  bgcolor: isConnected
                    ? alpha('#00ff88', 0.15)
                    : alpha('#ff4444', 0.15),
                  color: isConnected ? '#00ff88' : '#ff4444',
                  border: '1px solid',
                  borderColor: isConnected
                    ? alpha('#00ff88', 0.3)
                    : alpha('#ff4444', 0.3),
                }}
              />

              {/* Refresh Button */}
              <Tooltip title="Refresh data">
                <IconButton
                  onClick={handleRefresh}
                  disabled={refreshing}
                  sx={{
                    bgcolor: alpha('#00d4ff', 0.1),
                    border: '1px solid',
                    borderColor: alpha('#00d4ff', 0.2),
                    '&:hover': {
                      bgcolor: alpha('#00d4ff', 0.2),
                    },
                  }}
                >
                  <Refresh
                    sx={{
                      color: '#00d4ff',
                      animation: refreshing ? 'spin 1s linear infinite' : 'none',
                    }}
                  />
                </IconButton>
              </Tooltip>

              {/* Export Button */}
              <Tooltip title="Export (Coming Soon)">
                <IconButton
                  disabled
                  sx={{
                    bgcolor: alpha('#666', 0.1),
                    border: '1px solid',
                    borderColor: alpha('#666', 0.2),
                  }}
                >
                  <Download sx={{ color: 'grey.700' }} />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
        </Container>
      </Paper>

      {/* Main Content */}
      <Container maxWidth={false} sx={{ maxWidth: 1920, py: 3 }}>
        <Stack spacing={3}>
          {/* Error Alert */}
          {error && (
            <Alert 
              severity="error" 
              onClose={() => setError(null)}
              sx={{
                bgcolor: alpha('#ff4444', 0.1),
                border: '1px solid',
                borderColor: alpha('#ff4444', 0.3),
                color: '#ff4444',
              }}
            >
              {error}
            </Alert>
          )}

          {/* Stats Cards */}
          {stats && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  p: 2.5,
                  bgcolor: alpha('#0f1419', 0.6),
                  border: '1px solid',
                  borderColor: alpha('#00d4ff', 0.2),
                  borderRadius: 2,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1.5,
                      bgcolor: alpha('#00d4ff', 0.15),
                      border: '1px solid',
                      borderColor: alpha('#00d4ff', 0.3),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Timeline sx={{ color: '#00d4ff', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'grey.500', display: 'block', mb: 0.5 }}>
                      Total Alerts (7 days)
                    </Typography>
                    <Typography variant="h4" sx={{ color: 'grey.100', fontWeight: 700 }}>
                      {stats.total_alerts}
                    </Typography>
                  </Box>
                </Stack>
              </Paper>

              <Paper
                elevation={0}
                sx={{
                  flex: 1,
                  p: 2.5,
                  bgcolor: alpha('#0f1419', 0.6),
                  border: '1px solid',
                  borderColor: alpha('#00ff88', 0.2),
                  borderRadius: 2,
                }}
              >
                <Stack direction="row" alignItems="center" spacing={2}>
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: 1.5,
                      bgcolor: alpha('#00ff88', 0.15),
                      border: '1px solid',
                      borderColor: alpha('#00ff88', 0.3),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <CheckCircle sx={{ color: '#00ff88', fontSize: 24 }} />
                  </Box>
                  <Box>
                    <Typography variant="caption" sx={{ color: 'grey.500', display: 'block', mb: 0.5 }}>
                      Success Rate
                    </Typography>
                    <Typography variant="h4" sx={{ color: '#00ff88', fontWeight: 700 }}>
                      {stats.success_rate}%
                    </Typography>
                  </Box>
                </Stack>
              </Paper>
            </Stack>
          )}

          {/* Search and Filters */}
          <Paper
            elevation={0}
            sx={{
              p: 3,
              bgcolor: alpha('#0f1419', 0.6),
              border: '1px solid',
              borderColor: alpha('#00d4ff', 0.1),
              borderRadius: 2,
            }}
          >
            <Stack spacing={2.5}>
              {/* Search */}
              <TextField
                fullWidth
                placeholder="Search by symbol, strategy, or condition..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ color: 'grey.600' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    bgcolor: alpha('#0a0e14', 0.6),
                    color: 'grey.100',
                    '& fieldset': {
                      borderColor: alpha('#00d4ff', 0.2),
                    },
                    '&:hover fieldset': {
                      borderColor: alpha('#00d4ff', 0.4),
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: '#00d4ff',
                    },
                  },
                }}
              />

              {/* Tabs */}
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    minHeight: 48,
                    color: 'grey.500',
                  },
                  '& .Mui-selected': {
                    color: '#00d4ff',
                  },
                  '& .MuiTabs-indicator': {
                    backgroundColor: '#00d4ff',
                    height: 3,
                  },
                }}
              >
                <Tab label={`All Alerts (${alerts.length})`} />
                <Tab label={`Active (${alerts.filter(a => a.status === 'active').length})`} />
                <Tab label={`Executed (${alerts.filter(a => a.status === 'executed').length})`} />
                <Tab label={`Closed (${alerts.filter(a => a.status === 'closed').length})`} />
              </Tabs>
            </Stack>
          </Paper>

          {/* Debug Info */}
          {/* <Paper
            elevation={0}
            sx={{
              p: 2,
              bgcolor: alpha('#fb923c', 0.1),
              border: '1px solid',
              borderColor: alpha('#fb923c', 0.3),
              borderRadius: 2,
            }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <ErrorOutline sx={{ color: '#fb923c', fontSize: 20 }} />
              <Typography variant="body2" sx={{ color: '#fb923c', fontWeight: 600 }}>
                Debug: Showing {filteredAlerts.length} alerts out of {alerts.length} fetched 
                {alerts.length === 0 && ' (No data from API - check console for errors)'}
              </Typography>
            </Stack>
          </Paper> */}

          {/* Alerts List */}
          {filteredAlerts.length === 0 ? (
            <Paper
              elevation={0}
              sx={{
                p: 8,
                bgcolor: alpha('#0f1419', 0.6),
                border: '1px solid',
                borderColor: alpha('#00d4ff', 0.1),
                borderRadius: 2,
                textAlign: 'center',
              }}
            >
              <CheckCircle sx={{ fontSize: 64, color: 'grey.800', mb: 2 }} />
              <Typography variant="h6" sx={{ color: 'grey.600', fontWeight: 600, mb: 1 }}>
                {alerts.length === 0 ? 'No alerts in database' : 'No alerts found'}
              </Typography>
              <Typography variant="body2" sx={{ color: 'grey.700', mb: 2 }}>
                {alerts.length === 0 
                  ? 'Alerts will appear here once conditions are met and signals are triggered'
                  : 'Try adjusting your filters or search query'}
              </Typography>
              {alerts.length === 0 && (
                <Typography variant="caption" sx={{ color: 'grey.800', display: 'block', fontFamily: 'monospace' }}>
                  Check browser console for API response details
                </Typography>
              )}
            </Paper>
          ) : (
            <>
              <Stack spacing={2}>
                {filteredAlerts.map((alert, index) => (
                  <AlertCard 
                    key={alert.id || `${alert.strategy_id}-${alert.timestamp}-${index}`} 
                    alert={alert} 
                  />
                ))}
              </Stack>

              {/* Pagination */}
              {totalPages > 1 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: alpha('#0f1419', 0.6),
                    border: '1px solid',
                    borderColor: alpha('#00d4ff', 0.1),
                    borderRadius: 2,
                    display: 'flex',
                    justifyContent: 'center',
                  }}
                >
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    size="large"
                    sx={{
                      '& .MuiPaginationItem-root': {
                        color: 'grey.400',
                        borderColor: alpha('#00d4ff', 0.2),
                        '&:hover': {
                          bgcolor: alpha('#00d4ff', 0.1),
                          borderColor: alpha('#00d4ff', 0.4),
                        },
                        '&.Mui-selected': {
                          bgcolor: alpha('#00d4ff', 0.2),
                          color: '#00d4ff',
                          borderColor: alpha('#00d4ff', 0.4),
                          fontWeight: 700,
                          '&:hover': {
                            bgcolor: alpha('#00d4ff', 0.25),
                          },
                        },
                      },
                    }}
                  />
                </Paper>
              )}
            </>
          )}
        </Stack>
      </Container>

      {/* CSS Animations */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </Box>
  );
};

export default OfficerAlerts;
